// ─── Crafting Comb ───────────────────────────────────────────────────────────
//
// Two surfaces, one app. The customer page is the root, because that is the
// link a customer is given; the business calendar lives at /diary.
//
// Separated by path rather than by a toggle, so a customer cannot reach the
// business view by clicking something. This is presentation only — the real
// boundary is the API's route prefixes, and eventually a login. Said here so
// nobody mistakes the routing for the security.

import { Suspense, lazy, useEffect, useState } from "react";
import "@nulife/booking-ui/theme.css";
import "@nulife/calendar-ui/theme.css";
// ── The back office is not on the customer's phone ──────────────────────────
//
// `Business` pulls in FullCalendar, the time grid, the clients screen, the
// setup area and the registration forms. `CaptureApp` pulls in the capture
// library. None of it is ever shown to a customer booking a haircut — and
// until 2026-09-15 all of it was in the one chunk every visitor downloaded,
// 573kB minified, on whatever signal they had outside the salon.
//
// Loaded on demand instead. The customer page is the public one and the only
// one a stranger ever sees, so it is the one that should be small.
//
// `SignInForm` stays eager on purpose: it is tiny, and it is the first thing
// rendered on `/diary` for somebody who is not signed in. Deferring it would
// trade a large win for a visible flash on the exact screen Carrie opens every
// morning.
const Business = lazy(() => import("./Business.js").then(m => ({ default: m.Business })));
const CaptureApp = lazy(() => import("./CaptureApp.js").then(m => ({ default: m.CaptureApp })));
const Home = lazy(() => import("./Home.js").then(m => ({ default: m.Home })));
import { SignInForm, useAsync, Loading, Message } from "@nulife/app-ui";
import { Customer } from "./Customer.js";
import * as api from "./api.js";
import "@nulife/home-ui/theme.css";

function usePath(): string {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return path;
}

export function App() {
  const path = usePath();
  // ── The surfaces ────────────────────────────────────────────────────────
  //
  // `/home` is the tile grid, and it is where signing in lands. It replaced a
  // footer of text links; the tiles are the navigation now.
  //
  // `/clients` and `/calendar` are ENTRY POINTS rather than full two-way
  // routing: they open the diary with that panel already showing, which is
  // what a tile has to do. Pressing the diary's own buttons afterwards does
  // not rewrite the URL — said plainly rather than implied, because a URL that
  // silently stops describing the screen is worse than one that never claimed
  // to.
  const home = path === "/home" || path === "/home/";
  const business = path.startsWith("/diary")
    || path.startsWith("/clients") || path.startsWith("/calendar");
  const capture = path.startsWith("/capture");
  const initialPanel = path.startsWith("/clients") ? "clients" as const
    : path.startsWith("/calendar") ? "calendar" as const
    : null;

  const go = (to: string) => {
    window.history.pushState({}, "", to);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  const config = useAsync(() => api.getSettings(), []);

  // Who is signed in. Asked on every load, and re-asked whenever the server
  // says 401 — a session that ends mid-use returns the diary to the sign-in
  // form instead of leaving a screen of broken panels.
  const me = useAsync(() => api.getMe(), []);
  useEffect(() => { api.whenSignedOut(() => me.reload()); }, [me.reload]);

  // Nothing renders until the business's timezone is known. Rendering first
  // and correcting afterwards would show every time on the viewer's clock for
  // a moment — and a customer who reads the page in that moment is reading
  // wrong times.
  if (!config.data) {
    return (
      <Shell>
        {config.error
          ? <Message tone="bad">Could not load the salon's details. Please refresh.</Message>
          : <Loading />}
      </Shell>
    );
  }

  return (
    <Shell theme={config.data.theme}>
      {home || business || capture
        ? (me.data?.signedIn
            // One boundary around all of them: they are alternatives, never
            // shown together, and a second fallback would only add a way for
            // them to disagree about what loading looks like.
            ? (
              <Suspense fallback={<Loading />}>
                {home
                  ? <Home me={me.data} onGo={go} onSignedOut={() => me.reload()} />
                  : capture
                    ? <CaptureApp me={me.data} onSignedOut={() => me.reload()} />
                    : <Business
                        config={config.data}
                        me={me.data}
                        initialPanel={initialPanel}
                        onSignedOut={() => me.reload()}
                      />}
              </Suspense>
            )
            // The SAME gate, reusing the same session — signing in on either
            // surface signs you in for both, because it is one cookie and one
            // account. The customer page is never gated; only these two are.
            : <SignInForm
                organisationName={config.data.businessName}
                heading="Salon diary"
                submit={api.signIn}
                onSignedIn={() => me.reload()}
                resetHint={<>
                  Forgotten it? It can be reset on the machine that runs the
                  salon: <code>npm run set-password</code>.
                </>}
              />)
        : <Customer config={config.data} />}
      {/* ── The way back ───────────────────────────────────────────────────
          The footer used to be the navigation: three text links to the three
          surfaces. The tile grid on `/home` is that now, so all this has to do
          is get back to it — and `/home` carries its own footer instead. */}
      {!home && (
        <footer style={{
          marginTop: 40, color: "var(--nl-ink-dim)", fontSize: 13,
          display: "flex", gap: 16, flexWrap: "wrap",
        }}>
          {me.data?.signedIn && (
            <a
              href="/home"
              style={{ color: "inherit" }}
              data-testid="go-home"
              onClick={e => { e.preventDefault(); go("/home"); }}
            >Home</a>
          )}
          {(business || capture) && (
            <a
              href="/"
              style={{ color: "inherit" }}
              data-testid="go-customer"
              onClick={e => { e.preventDefault(); go("/"); }}
            >Customer booking page</a>
          )}
        </footer>
      )}
    </Shell>
  );
}

/**
 * The app's visual root.
 *
 * ── `data-nl-theme` is not decoration; without it there is no styling ───────
 *
 * Every token in `@nulife/booking-ui`'s theme.css is declared under
 * `[data-nl-theme] { … }`. Nothing in this app set that attribute, so not one
 * of those custom properties resolved — every `var(--nl-ink)`,
 * `var(--nl-surface)` and `var(--nl-state-open)` in every component fell back
 * to nothing, and the whole product rendered as unstyled black text on white.
 * The calendar was the most obvious casualty, being almost entirely token-
 * coloured.
 *
 * It is set ONCE, here, above both surfaces rather than on each — the theme is
 * a property of the product, and two places to set it is one place to forget.
 * `background` below is what paints the teal ground, and it needs the tokens
 * to exist to do it.
 *
 * ── The value is the TENANT'S palette ──────────────────────────────────────
 *
 * It comes from `organisations.theme` in the database, not from a constant
 * here — the organisation-level colour override issue #9 asked for. Comb &
 * Craft is on a provisional olive-green trial; anything with no theme set
 * falls to the platform's own teal, which is what every other tenant gets
 * until somebody chooses otherwise.
 *
 * `"nulife"` when unset rather than omitting the attribute: the tokens are
 * declared under `[data-nl-theme]`, so the attribute has to be PRESENT even
 * when there is nothing to override. That is the bug this comment was
 * originally written about.
 */
function Shell({ theme, children }: { theme?: string | null; children: React.ReactNode }) {
  return (
    <div
      data-nl-theme={theme || "nulife"}
      style={{
        minHeight: "100vh",
        background: "var(--nl-bg)",
        color: "var(--nl-ink)",
        // `fontFamily`, not `font`. The `font` shorthand REQUIRES a size and
        // a family together; given only a family the whole declaration is
        // invalid and dropped, so the app silently kept the browser default.
        fontFamily: "var(--nl-font)",
        fontSize: "1rem",
        lineHeight: 1.45,
      }}
    >
      <div style={{ maxWidth: 880, margin: "0 auto", padding: "20px 16px 64px" }}>
        {children}
      </div>
    </div>
  );
}
